import {DateRange, DateTime, TimeSpan, TimeZone} from 'util/time';
import {User} from './auth';
import {Space, SpaceAnalyticsGroup, SpaceAnalyticsValues} from 'services/entities/spaces';
import {Design} from 'services/entities/designs';
import {Currency, CurrencyAmount} from 'util/currency';
import {Discount} from 'services/entities/discounts';

export interface BookingInsuranceOptions {
  type: string;
  foodDrink: 'yes'|'no';
  hasInsurance: 'yes'|'no'|'not-sure';
}

export enum BookingService {
  Ideas = 'ideas',
  Design = 'design',
  BrandAssets = 'brandAssets',
  Video = 'video',
  Logo = 'logo',
  Everything = 'everything',
}

export enum BookingField {
  DailyAnalytics = 'daily_analytics',
  AggregatedAnalytics = 'aggregated_analytics',
  OutstandingAmount = 'outstanding_amount',
  OutstandingDueDate = 'outstanding_due_date',
  Price = 'price',
  Tax = 'tax',
  TotalPrice = 'total_price',
  LandlordSplit = 'landlord_split',
  SookSplit = 'sook_split'
}

export interface BookingProduct {
  sku: string;
  quantity: number;
  optionValues: {[index:string]:string};
}

export interface BookingTime {
  startTime: DateTime;
  endTime: DateTime;
  includeFlexiTicket: boolean;
}

export interface BookingBase {
  space: string;
  design: string|null;
  products: BookingProduct[];
  discounts: string[];
}

export interface NewBooking extends BookingBase {
  times: BookingTime[];
}

export interface DraftBooking extends NewBooking {
  code: string|null;
}

export interface Booking extends Omit<BookingBase, 'discounts'> {
  code: string;
  discounts: Discount[];
  startTime: DateTime;
  endTime: DateTime;
  creator: User;
  created: DateTime;
  occupationalLicenseURL: URL|null;
  currency: Currency;
  dailyAnalytics: SpaceAnalyticsGroup[]|null;
  aggregatedAnalytics: SpaceAnalyticsValues|null;
}

export interface ExpandedBooking extends Omit<Booking, 'space'|'design'> {
  space: Space;
  design: Design|null;
  outstandingAmount: CurrencyAmount|null;
  outstandingDueDate: DateTime|null;
}

export interface LandlordBooking extends Booking {
  price: CurrencyAmount;
  tax: CurrencyAmount;
  totalPrice: CurrencyAmount;
  landlordSplit: CurrencyAmount;
  sookSplit: CurrencyAmount;
  outstandingAmount: CurrencyAmount|null;
}

export interface BookingsFilters {
  minTime?: DateTime;
  maxTime?: DateTime;
  space?: string;
  design?: string;
}

export interface BookingTimes {
  start: DateTime;
  end: DateTime;
}

export interface BookingTimesFilters extends BookingsFilters {
  ignoreCode?: string;
  space?: string;
}

export interface BookingSettings {
  minTime: TimeSpan,
  maxTime: TimeSpan,
  minDuration: TimeSpan,
  priceDuration: TimeSpan,
  increment: TimeSpan,
  minDateOffset: number,
  defaultTimeZone: TimeZone,
  finalPaymentDateOffset: number,
  flexiTicketRate: number
  flexiTicketMinDateOffset: number,
}

export interface LandlordBookingEarningStats {
  subtotal: CurrencyAmount;
  taxes: CurrencyAmount;
  total: CurrencyAmount;
}

export interface LandlordBookingTotalsStats {
  count: number;
  duration: TimeSpan;
}

export interface LandlordBookingDealStats {
  count: number;
  averageBookings: number;
}

export interface LandlordBookingBookingsStats extends LandlordBookingEarningStats, LandlordBookingTotalsStats {
  averageDuration: TimeSpan;
  occupancyRate: number;
  vacancyRate: number;
}

export interface LandlordBookingRevenueStats extends LandlordBookingEarningStats, LandlordBookingTotalsStats {
  averagePerMonth: CurrencyAmount;
  averagePerWeek: CurrencyAmount;
  averagePerDeal: CurrencyAmount;
  averagePerBooking: CurrencyAmount;
}

export interface LandlordBookingStats {
  bookings: LandlordBookingBookingsStats;
  deals: LandlordBookingDealStats;
  revenue: LandlordBookingRevenueStats;
  landlordSplit: LandlordBookingEarningStats;
  sookSplit: LandlordBookingEarningStats;
}

export interface LandlordRevenuePeriod {
  revenue: CurrencyAmount;
  dates: DateRange;
}